import {
  buildGamePlatformApiPath,
  buildGamePlatformRoomApiPath,
} from "./routes";
import type {
  ApiResponse,
  CreateGamePlatformRoomRequest,
  CreateReplayShareRequest,
  GameCatalogResponse,
  GamePlatformEvent,
  GamePlatformRoom,
  ReplayShare,
  ResolvedShareLink,
  ShareLink,
  SubmitCommandAck,
  SubmitCommandRequest,
  ViewerState,
} from "./types";

type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface GamePlatformApiClientOptions {
  readonly baseUrl?: string;
  readonly fetchImpl?: FetchLike;
  readonly defaultHeaders?: HeadersInit | (() => HeadersInit);
}

export interface GamePlatformApiClient {
  readonly getCatalog: () => Promise<ApiResponse<GameCatalogResponse>>;
  readonly listRooms: (
    gameType?: string,
  ) => Promise<ApiResponse<readonly GamePlatformRoom[]>>;
  readonly listMyRooms: (
    gameType?: string,
  ) => Promise<ApiResponse<readonly GamePlatformRoom[]>>;
  readonly createRoom: (
    input: CreateGamePlatformRoomRequest,
  ) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly getRoom: (roomId: string) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly cancelRoom: (
    roomId: string,
  ) => Promise<ApiResponse<{ readonly message: string }>>;
  readonly joinRoom: (roomId: string) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly joinObserver: (
    roomId: string,
  ) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly startRoom: (
    roomId: string,
  ) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly submitCommand: (
    roomId: string,
    command: SubmitCommandRequest,
  ) => Promise<ApiResponse<SubmitCommandAck>>;
  readonly getEvents: (
    roomId: string,
    after?: number,
  ) => Promise<ApiResponse<readonly GamePlatformEvent[]>>;
  readonly getViewerStates: (
    roomId: string,
    after?: number,
  ) => Promise<ApiResponse<readonly ViewerState[]>>;
  readonly createShareLink: (
    roomId: string,
    clientBaseUrl?: string,
  ) => Promise<ApiResponse<ShareLink>>;
  readonly createObserverShareLink: (
    roomId: string,
    clientBaseUrl?: string,
  ) => Promise<ApiResponse<ShareLink>>;
  readonly resolveShareLink: (
    gameType: string,
    roomNumber: string,
    secret: string,
  ) => Promise<ApiResponse<ResolvedShareLink>>;
  readonly joinByShareLink: (
    gameType: string,
    roomNumber: string,
    secret: string,
  ) => Promise<ApiResponse<GamePlatformRoom>>;
  readonly createReplayShare: (
    input: CreateReplayShareRequest,
  ) => Promise<ApiResponse<ReplayShare>>;
  readonly resolveReplayShare: (
    code: string,
  ) => Promise<ApiResponse<ReplayShare>>;
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
}

function normalizeBaseUrl(baseUrl: string | undefined): string {
  return (baseUrl ?? "").replace(/\/+$/, "");
}

function headersFrom(value: HeadersInit | (() => HeadersInit) | undefined) {
  return typeof value === "function" ? value() : value;
}

function withQuery(
  path: string,
  query: Readonly<Record<string, string | number | undefined>>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

export function createGamePlatformApiClient(
  options: GamePlatformApiClientOptions = {},
): GamePlatformApiClient {
  const baseUrl = normalizeBaseUrl(options.baseUrl);
  const fetchImpl = options.fetchImpl ?? fetch;

  async function request<T>(
    path: string,
    requestOptions: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const body =
      requestOptions.body === undefined
        ? undefined
        : JSON.stringify(requestOptions.body);
    const response = await fetchImpl(`${baseUrl}${path}`, {
      method: requestOptions.method ?? "GET",
      credentials: "include",
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...headersFrom(options.defaultHeaders),
      },
      body,
    });
    return (await response.json()) as ApiResponse<T>;
  }

  return {
    getCatalog: () => request(buildGamePlatformApiPath("catalog")),
    listRooms: (gameType) =>
      request(withQuery(buildGamePlatformApiPath("rooms"), { gameType })),
    listMyRooms: (gameType) =>
      request(withQuery(buildGamePlatformApiPath("rooms/mine"), { gameType })),
    createRoom: (input) =>
      request(buildGamePlatformApiPath("rooms"), {
        method: "POST",
        body: {
          gameType: input.gameType,
          config: input.config ?? {},
          minPlayers: input.minPlayers,
          maxPlayers: input.maxPlayers,
          observerMode: input.observerMode,
          observerSeatCount: input.observerSeatCount,
        },
      }),
    getRoom: (roomId) => request(buildGamePlatformRoomApiPath(roomId)),
    cancelRoom: (roomId) =>
      request(buildGamePlatformRoomApiPath(roomId), {
        method: "DELETE",
      }),
    joinRoom: (roomId) =>
      request(buildGamePlatformRoomApiPath(roomId, "join"), {
        method: "POST",
      }),
    joinObserver: (roomId) =>
      request(buildGamePlatformRoomApiPath(roomId, "join-observer"), {
        method: "POST",
      }),
    startRoom: (roomId) =>
      request(buildGamePlatformRoomApiPath(roomId, "start"), {
        method: "POST",
      }),
    submitCommand: (roomId, command) =>
      request(buildGamePlatformRoomApiPath(roomId, "submit"), {
        method: "POST",
        body: command,
      }),
    getEvents: (roomId, after) =>
      request(
        withQuery(buildGamePlatformRoomApiPath(roomId, "events"), {
          after,
        }),
      ),
    getViewerStates: (roomId, after) =>
      request(
        withQuery(buildGamePlatformRoomApiPath(roomId, "viewer-states"), {
          after,
        }),
      ),
    createShareLink: (roomId, clientBaseUrl) =>
      request(buildGamePlatformRoomApiPath(roomId, "share-link"), {
        method: "POST",
        body: clientBaseUrl === undefined ? {} : { clientBaseUrl },
      }),
    createObserverShareLink: (roomId, clientBaseUrl) =>
      request(buildGamePlatformRoomApiPath(roomId, "observer-share-link"), {
        method: "POST",
        body: clientBaseUrl === undefined ? {} : { clientBaseUrl },
      }),
    resolveShareLink: (gameType, roomNumber, secret) =>
      request(
        withQuery(buildGamePlatformApiPath("share-links/resolve"), {
          gameType,
          roomNumber,
          secret,
        }),
      ),
    joinByShareLink: (gameType, roomNumber, secret) =>
      request(buildGamePlatformApiPath("share-links/join"), {
        method: "POST",
        body: { gameType, roomNumber, secret },
      }),
    createReplayShare: (input) =>
      request(buildGamePlatformApiPath("replay-shares"), {
        method: "POST",
        body: { gameType: input.gameType, events: input.events },
      }),
    resolveReplayShare: (code) =>
      request(
        buildGamePlatformApiPath(`replay-shares/${encodeURIComponent(code)}`),
      ),
  };
}
